import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Plus, FileText, ChevronDown, ChevronRight, CheckCircle, Lock, Target } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getActiveTasks, getTaskCategories, getTasksByCategory } from '../../config/taskRegistry';
import { Button, Card, Input, Select, Tooltip } from '../design-system';
import { apiPost, apiGet } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import therapeuticGoalsService from '../../services/therapeuticGoalsService';
import analyticsService from '../../services/analyticsService';

const CreateTaskModal = ({ isOpen, onClose, patients, onTaskCreated }) => {
  const { user, userInfo } = useAuth();
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedTaskType, setSelectedTaskType] = useState('');
  const [selectedGoalId, setSelectedGoalId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  
  // Dynamic form data for task assignment
  const [formData, setFormData] = useState({});
  
  // Category expansion state
  const [expandedCategories, setExpandedCategories] = useState({
    'intake': false,
    'wellness': false,
    'grief': false,
    'gratitude': false,
    'homework': false,
    'assessment': false,
    'custom': false
  });
  

  // Get available task types from new registry
  const taskTypes = getActiveTasks();
  const selectedTaskTypeData = selectedTaskType ? taskTypes.find(t => t.typeId === selectedTaskType) : null;
  const TaskComponent = selectedTaskTypeData?.component;
  
  // Get completed tasks for the selected patient to determine eligibility
  const selectedPatientData = patients?.find(p => p.userId === selectedPatient);
  const completedTaskIds = selectedPatientData?.completedTasks?.map(task => task.taskTypeId) || [];
  
  // For now, all tasks are eligible (we'll add prerequisites logic later)
  const eligibleTaskTypes = taskTypes;

  // Dynamic form rendering function
  const renderTaskAssignmentForm = () => {
    if (!TaskComponent?.AssignTaskForm) return null;
    
    return (
      <TaskComponent.AssignTaskForm 
        formData={formData}
        setFormData={setFormData}
        taskType={selectedTaskTypeData}
      />
    );
  };

  // Auto-select patient if only one is provided
  useEffect(() => {
    if (isOpen && patients && patients.length === 1) {
      setSelectedPatient(patients[0].userId);
    } else if (isOpen && patients && patients.length > 1) {
      setSelectedPatient('');
    }
  }, [isOpen, patients]);

  // Fetch therapeutic goals when patient is selected
  useEffect(() => {
    const fetchGoals = async () => {
      if (!selectedPatient || !isOpen) {
        setGoals([]);
        setSelectedGoalId('');
        return;
      }

      // Find patient data to get patientId
      const patientData = patients?.find(p => p.userId === selectedPatient);
      if (!patientData?.patientId) {
        setGoals([]);
        return;
      }

      try {
        setLoadingGoals(true);
        const response = await therapeuticGoalsService.getGoals(user, patientData.patientId);
        const activeGoals = (response.goals || []).filter(goal => 
          goal.status === 'active' || goal.status === 'in-progress'
        );
        setGoals(activeGoals);
      } catch (error) {
        console.error('Error fetching therapeutic goals:', error);
        // Don't show error toast, just silently fail - goals are optional
        setGoals([]);
      } finally {
        setLoadingGoals(false);
      }
    };

    fetchGoals();
  }, [selectedPatient, isOpen, patients, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    if (!selectedTaskType) {
      toast.error('Please select a task type');
      return;
    }

    // Validate form data if task has custom form
    if (TaskComponent?.AssignTaskForm) {
      // Add validation logic here based on task requirements
      if (selectedTaskType === 'task_013' && !formData.instruction?.trim()) {
        toast.error('Please enter an instruction for the open journaling task');
        return;
      }
      if (selectedTaskType === 'task_011' && (!formData.title?.trim() || !formData.description?.trim())) {
        toast.error('Please fill in both title and description for custom task');
        return;
      }
    }


    setIsSubmitting(true);
    
    try {
      // Use API utility to ensure consistent auth/headers/retries
      console.log('🔔 THERAPIST: Starting task assignment...');
      console.log('🔔 THERAPIST: Patient ID:', selectedPatient);
      console.log('🔔 THERAPIST: Task Type:', selectedTaskType);
      console.log('🔔 THERAPIST: Priority:', priority);

      const payload = {
        patientId: selectedPatient,
        taskTypeId: selectedTaskType,
        priority,
        dueDate: dueDate || null,
        goalId: selectedGoalId || null,
        ...(TaskComponent?.AssignTaskForm && {
          title: formData.title || selectedTaskTypeData?.name,
          description: formData.instruction || formData.description || selectedTaskTypeData?.description
        }),
        ...(!TaskComponent?.AssignTaskForm && {
          taskMetadata: {
            name: selectedTaskTypeData?.name,
            description: selectedTaskTypeData?.description,
            category: selectedTaskTypeData?.category,
            estimatedDuration: selectedTaskTypeData?.estimatedDuration
          }
        })
      };

      const result = await apiPost('/.netlify/functions/create-task', payload);
      console.log('✅ THERAPIST: Task created successfully:', result);
      console.log('🔔 THERAPIST: Notification should have been sent to patient');
      toast.success('Task created successfully!');
      
      // Track task assignment event
      try {
        analyticsService.track('task_assigned', {
          taskId: result.task?.taskId,
          taskType: selectedTaskTypeData?.name,
          taskTypeId: selectedTaskType,
          taskCategory: selectedTaskTypeData?.category || 'homework',
          patientId: selectedPatient,
          therapistId: userInfo?.userId,
          priority: priority,
          hasDueDate: !!dueDate,
          dueDate: dueDate || null,
          hasGoal: !!selectedGoalId,
          goalId: selectedGoalId || null
        });
      } catch (analyticsError) {
        // Don't break task creation if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
      
      onTaskCreated(result.task);
      handleClose();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(error.message || 'Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedPatient('');
    setSelectedTaskType('');
    setSelectedGoalId('');
    setPriority('medium');
    setDueDate('');
    setFormData({});
    setGoals([]);
    onClose();
  };

  const handleTaskTypeChange = (typeId) => {
    setSelectedTaskType(typeId);
    setFormData({}); // Reset form data when task type changes
  };


  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <Card className="max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto" variant="elevated">
              <div className="flex items-center justify-between p-4 md:p-6 border-b">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900">Create New Task</h2>
        <Button
          onClick={handleClose}
          variant="ghost"
          size="small"
          className="text-gray-400 hover:text-gray-600 p-1"
        >
          <X className="h-5 w-5 md:h-6 md:w-6" />
        </Button>
      </div>

        <form onSubmit={handleSubmit} className="p-3 md:p-6 space-y-4 md:space-y-6">
          {/* Patient Selection - Only show if multiple patients or no patient pre-selected */}
          {(patients?.length > 1 || !selectedPatient) && (
            <Select
              label="Select Patient"
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
              options={[
                { value: '', label: 'Choose a patient...' },
                ...patients.map((patient) => ({
                  value: patient.userId,
                  label: `${patient.name || 'Unnamed Patient'} (${patient.email})`
                }))
              ]}
              required
            />
          )}

          {/* Show selected patient info if pre-selected */}
          {selectedPatient && patients?.length === 1 && selectedPatientData && (
            <Card className="p-4" style={{backgroundColor: 'rgba(44, 166, 164, 0.1)', border: '1px solid rgba(44, 166, 164, 0.2)'}}>
              <div className="flex items-center">
                <User className="h-5 w-5 mr-2" style={{color: 'var(--color-primary)'}} />
                <div>
                  <p className="text-sm font-medium" style={{color: 'var(--color-primary)'}}>
                    Task for: {selectedPatientData.name || 'Unnamed Patient'}
                  </p>
                  <p className="text-sm" style={{color: 'var(--color-primary)', opacity: 0.8}}>{selectedPatientData.email}</p>
                </div>
              </div>
            </Card>
          )}

          {/* Task Type Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                <Clock className="h-4 w-4 inline mr-1" />
                Task Type
              </label>
            </div>

            {/* Predefined Task Types */}
              <div className="space-y-4">
                {Object.entries(getTaskCategories()).map(([categoryKey, categoryName]) => {
                  const categoryTasks = getTasksByCategory(categoryKey);
                  if (categoryTasks.length === 0) return null;
                  
                  const isExpanded = expandedCategories[categoryKey];
                  const eligibleTasks = categoryTasks.filter(task => 
                    eligibleTaskTypes.some(eligible => eligible.typeId === task.typeId)
                  );
                  const completedTasks = categoryTasks.filter(task => 
                    completedTaskIds.includes(task.typeId)
                  );
                  const lockedTasks = categoryTasks.filter(task => 
                    !eligibleTaskTypes.some(eligible => eligible.typeId === task.typeId) && 
                    !completedTaskIds.includes(task.typeId)
                  );
                  
                  return (
                    <div key={categoryKey} className="border border-gray-200 rounded-lg">
                      <button
                        type="button"
                        onClick={() => toggleCategory(categoryKey)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          )}
                          <h3 className="text-sm font-medium text-gray-900 truncate">{categoryName}</h3>
                          <div className="flex items-center space-x-1 md:space-x-2">
                            {eligibleTasks.length > 0 && (
                              <Tooltip content={`${eligibleTasks.length} tasks available for assignment`} position="top">
                                <span className="text-xs px-1.5 md:px-2 py-1 rounded font-medium" style={{backgroundColor: 'var(--color-success)', color: 'white'}}>
                                  <span className="hidden md:inline">{eligibleTasks.length} Available</span>
                                  <span className="md:hidden">{eligibleTasks.length}</span>
                                </span>
                              </Tooltip>
                            )}
                            {completedTasks.length > 0 && (
                              <Tooltip content={`${completedTasks.length} tasks already completed`} position="top">
                                <div className="flex items-center">
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                  <span className="hidden md:inline ml-1 text-xs px-1.5 py-1 rounded font-medium" style={{backgroundColor: 'var(--color-primary)', color: 'white'}}>
                                    {completedTasks.length} Completed
                                  </span>
                                  <span className="md:hidden ml-1 text-xs px-1.5 py-1 rounded font-medium" style={{backgroundColor: 'var(--color-primary)', color: 'white'}}>
                                    {completedTasks.length}
                                  </span>
                                </div>
                              </Tooltip>
                            )}
                            {lockedTasks.length > 0 && (
                              <Tooltip content={`${lockedTasks.length} tasks locked (prerequisites required)`} position="top">
                                <div className="flex items-center">
                                  <Lock className="h-4 w-4 text-gray-500" />
                                  <span className="hidden md:inline ml-1 text-xs px-1.5 py-1 rounded font-medium" style={{backgroundColor: 'var(--color-accent)', color: 'var(--color-text)'}}>
                                    {lockedTasks.length} Locked
                                  </span>
                                  <span className="md:hidden ml-1 text-xs px-1.5 py-1 rounded font-medium" style={{backgroundColor: 'var(--color-accent)', color: 'var(--color-text)'}}>
                                    {lockedTasks.length}
                                  </span>
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </button>
                      
                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-2">
                          {categoryTasks.map((task) => {
                            const isEligible = eligibleTaskTypes.some(eligible => eligible.typeId === task.typeId);
                            const isCompleted = completedTaskIds.includes(task.typeId);
                            
                            return (
                              <label
                                key={task.typeId}
                                className={`flex items-start p-3 md:p-4 border rounded-lg transition-colors ${
                                  !isEligible && !isCompleted
                                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                    : selectedTaskType === task.typeId
                                    ? 'cursor-pointer'
                                    : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                }`}
                                style={selectedTaskType === task.typeId ? {
                                  borderColor: 'var(--color-primary)',
                                  backgroundColor: 'rgba(44, 166, 164, 0.1)'
                                } : {}}
                              >
                                <input
                                  type="radio"
                                  name="taskType"
                                  value={task.typeId}
                                  checked={selectedTaskType === task.typeId}
                                  onChange={() => handleTaskTypeChange(task.typeId)}
                                  disabled={!isEligible && !isCompleted}
                                  className="h-4 w-4 md:h-5 md:w-5 border-gray-300 disabled:opacity-50 mt-0.5 flex-shrink-0"
                                  style={{
                                    accentColor: 'var(--color-primary)'
                                  }}
                                />
                                <div className="ml-3 flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <p className="text-sm font-medium text-gray-900">{task.name}</p>
                                      {isCompleted && (
                                        <Tooltip content="This task has been completed" position="top">
                                          <div className="flex items-center">
                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                            <span className="hidden md:inline ml-1 text-xs px-2 py-1 rounded" style={{backgroundColor: 'var(--color-success)', color: 'white'}}>
                                              Completed
                                            </span>
                                          </div>
                                        </Tooltip>
                                      )}
                                      {!isEligible && !isCompleted && (
                                        <Tooltip content="This task is locked (prerequisites required)" position="top">
                                          <div className="flex items-center">
                                            <Lock className="h-4 w-4 text-gray-500" />
                                            <span className="hidden md:inline ml-1 text-xs px-2 py-1 rounded" style={{backgroundColor: 'var(--color-accent)', color: 'var(--color-text)'}}>
                                              Locked
                                            </span>
                                          </div>
                                        </Tooltip>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500">{task.estimatedDuration} min</span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                  {!isEligible && !isCompleted && task.prerequisites && task.prerequisites.length > 0 && (
                                    <p className="text-xs text-amber-600 mt-1">
                                      Requires: {task.prerequisites.map(prereq => {
                                        const prereqTask = taskTypes.find(t => t.typeId === prereq);
                                        return prereqTask?.name || prereq;
                                      }).join(', ')}
                                    </p>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            {/* Dynamic Task Assignment Form */}
            {renderTaskAssignmentForm()}
          </div>

          {/* Therapeutic Goal Selection */}
          {selectedPatient && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Target className="h-4 w-4 inline mr-1" />
                Link to Therapeutic Goal (Optional)
              </label>
              {loadingGoals ? (
                <div className="text-sm text-gray-500 py-2">Loading goals...</div>
              ) : goals.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">
                  No active therapeutic goals found for this patient. 
                  <span className="text-xs block mt-1">
                    You can add goals in the patient detail page.
                  </span>
                </div>
              ) : (
                <Select
                  value={selectedGoalId}
                  onChange={(e) => setSelectedGoalId(e.target.value)}
                  options={[
                    { value: '', label: 'No goal (optional)' },
                    ...goals.map((goal) => ({
                      value: goal.goalId,
                      label: `${goal.title}${goal.status === 'in-progress' ? ' (In Progress)' : ''}`
                    }))
                  ]}
                />
              )}
              {selectedGoalId && (
                <p className="text-xs text-gray-600 mt-1 italic">
                  This task will help the patient work towards: {goals.find(g => g.goalId === selectedGoalId)?.title}
                </p>
              )}
            </div>
          )}

          {/* Task Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' }
              ]}
            />
            <Input
              type="date"
              label="Due Date (Optional)"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              size="medium"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !selectedPatient || !selectedTaskType}
              variant="primary"
              size="medium"
            >
              {isSubmitting ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateTaskModal; 